// 商户后台侧边导航：消费 src/menu.tsx 数据（C1.3 冻结表 7 组 23 项）
// 组级 empty-check：组内叶全被权限过滤后整组隐藏（防空组）；
// badge 由 useMerchantBadges 动态覆盖（禁写死）；壳基路径角标挂接见 dynamicBadgeFor。
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { CreditCard, ChevronDown } from 'lucide-react';
import { MENU, type LeafItem } from '@/menu';
import { loadSession } from '@/api/client';
import { useMerchantBadges, type MerchantBadges } from '@/hooks/useMerchantBadges';
import { useNoticeSound } from '@/hooks/useNoticeSound';
import { hasMerchantPermission, merchantPermissionsOf } from '@/lib/hasMerchantPermission';

/**
 * 壳基路径角标挂接（freeze 表：badge path 键改壳基路径，null 隐藏语义不变）：
 * - 通道业务壳 /merchant/channel ← topup in-flight 计数（原 /merchant/channel/topup 键迁移）
 * - 规则与处置壳 /merchant/risk/rules ← 快捷处置计数（原 quick-actions 键迁移；
 *   处置历史≠待办，计数恒 0 → 角标隐藏，语义与迁移前一致）
 * - 审批中心独立一级菜单（硬约束不动）
 */
function dynamicBadgeFor(path: string, badges: MerchantBadges): number | null {
  switch (path) {
    case '/merchant/channel':
      return badges.topupInflightCount;
    case '/merchant/risk/rules':
      return badges.quickActionPendingCount;
    // PAGE-MCH-USER-015：审批中心角标 = APC-002 五类型 pending 总数（动态计数，禁写死）
    case '/merchant/approvals':
      return badges.approvalCenterPendingCount;
    default:
      return null;
  }
}

export default function Sidebar() {
  const { pathname } = useLocation();
  const badges = useMerchantBadges(pathname);
  // 提示音单实例挂载（fleet sound-merchant）：审批/未读两链轮询判增即响；
  // Header 亦消费 useMerchantBadges 但禁挂本 hook（防双响），见 hooks/useNoticeSound.ts 头注释
  useNoticeSound();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const permissions = merchantPermissionsOf(loadSession());

  const toggle = (label: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <aside className="w-60 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0">
      {/* 品牌：Allinks 商户后台（UI-R06 合规：白标口径，禁用演示品牌名） */}
      <div className="px-5 py-5 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-slate-900 font-bold text-sm leading-tight">Allinks 商户后台</p>
            <p className="text-slate-400 text-xs">商户收单与发卡控制台</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2 px-2.5">
        {MENU.map((item) => {
          const isCollapsed = collapsed.has(item.label);
          // 权限过滤（S5）：配置了 permissionCodes 的叶仅在命中任一码时渲染；
          // 组级 empty-check：过滤后叶为空 → 整组隐藏（防空组）
          const visibleLeaves = item.leaves
            .filter((leaf) => !leaf.permissionCodes || hasMerchantPermission(permissions, leaf.permissionCodes));
          if (visibleLeaves.length === 0) {
            return null;
          }
          return (
            <div key={item.label} className="mb-0.5">
              <button
                onClick={() => toggle(item.label)}
                className="w-full flex items-center justify-between px-2.5 py-2 text-slate-500 hover:text-slate-700 transition-colors"
              >
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                  {item.icon}
                  {item.label}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
              </button>
              {!isCollapsed && (
                <div className="space-y-0.5 mt-0.5">
                  {visibleLeaves.map((leaf: LeafItem) => {
                    const badge = dynamicBadgeFor(leaf.path, badges) ?? leaf.badge;
                    return (
                      <NavLink
                        key={leaf.path}
                        to={leaf.path}
                        className={({ isActive }) =>
                          `w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-brand-50 text-brand-700'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`
                        }
                      >
                        <span>{leaf.label}</span>
                        {badge ? (
                          <span className="min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] text-white font-medium flex items-center justify-center">
                            {badge}
                          </span>
                        ) : null}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="px-4 py-3 border-t border-slate-100 text-[11px] text-slate-400">
        <p>Allinks Merchant Console</p>
        <p>v0.5.0 · 分组菜单（C1.3）</p>
      </div>
    </aside>
  );
}
